import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { useCreateEinrichtung, useUpdateEinrichtung } from '@/api/useEinrichtung';
import type { Einrichtung, UUID } from '@/api/types';

interface EinrichtungEditDialogProps {
  opened: boolean;
  traegerId: UUID | null;
  einrichtung: Einrichtung | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  konzept_typ: string;
}

function describeError(err: unknown): string {
  const ax = err as AxiosError<{ detail?: string | { message?: string } }> | undefined;
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail && detail.message) {
    return String(detail.message);
  }
  return ax?.message ?? '';
}

export function EinrichtungEditDialog({
  opened,
  traegerId,
  einrichtung,
  onClose,
}: EinrichtungEditDialogProps) {
  const { t } = useTranslation(['einrichtung', 'common', 'errors']);
  const create = useCreateEinrichtung();
  const update = useUpdateEinrichtung();
  const isEdit = einrichtung !== null;

  const form = useForm<FormValues>({
    initialValues: {
      name: einrichtung?.name ?? '',
      konzept_typ: (einrichtung as Einrichtung & { konzept_typ?: string } | null)?.konzept_typ ?? '',
    },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : t('einrichtung:validation.nameRequired')),
      konzept_typ: (v) =>
        v.trim().length > 0 ? null : t('einrichtung:validation.konzeptRequired'),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        name: einrichtung?.name ?? '',
        konzept_typ:
          (einrichtung as (Einrichtung & { konzept_typ?: string }) | null)?.konzept_typ ?? '',
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, einrichtung]);

  const submit = form.onSubmit(async (values) => {
    try {
      if (isEdit && einrichtung) {
        await update.mutateAsync({
          id: einrichtung.id,
          patch: { name: values.name, konzept_typ: values.konzept_typ },
        });
        notifications.show({ color: 'teal', message: t('einrichtung:info.updated') });
      } else {
        if (!traegerId) return;
        await create.mutateAsync({
          traeger_id: traegerId,
          name: values.name,
          konzept_typ: values.konzept_typ,
        });
        notifications.show({ color: 'teal', message: t('einrichtung:info.created') });
      }
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeError(err) || t('errors:generic'),
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('einrichtung:form.edit.title') : t('einrichtung:form.create.title')}
      centered
    >
      <form onSubmit={submit}>
        <Stack>
          <TextInput
            label={t('einrichtung:form.field.name')}
            placeholder={t('einrichtung:form.field.namePlaceholder')}
            required
            {...form.getInputProps('name')}
          />
          <TextInput
            label={t('einrichtung:form.field.konzept')}
            placeholder={t('einrichtung:form.field.konzeptPlaceholder')}
            required
            {...form.getInputProps('konzept_typ')}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {t('common:actions.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
